import { forwardRef, useCallback, useState, useImperativeHandle } from 'react';
import { ISource } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import SpinnerOverlay from '../Common/SpinnerOverlay';
import { Body2, Button, shorthands, Subtitle1, Title1, Title2, Title3, Tooltip } from '@fluentui/react-components';
import { makeStyles } from '../../Utils/makeStylesWrapper';
import './SourcePreview.scss';
import { Open24Filled } from '@fluentui/react-icons';
import { EllipsisText } from '../Common/EllipsisText';
import { useNotification } from '../../Contexts/NotificationContext';

export interface SourcePreviewProps {
    source: ISource;
    conversationId: string;
}

export interface SourcePreviewRef {
    openSource: () => Promise<void>;
}

const useStyles = makeStyles({
    sourceContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('1rem'),
    },
    headerContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
    },
    body: {
        whiteSpace: 'pre-line',
    },
});

// Component to display a preview of a source (e.g. for RAG tools)
export const SourcePreview = forwardRef<SourcePreviewRef, SourcePreviewProps>(function SourcePreview({ source, conversationId }: SourcePreviewProps, ref) {
    const classes = useStyles();
    const [isLoading, setIsLoading] = useState(false);
    const { dispatchNotification } = useNotification();

    // Function to open the source in a new tab
    const openSource = async () => {
        await performEnterpriseGPTApiCall(async (client) => {
            try {
                // if the source is a website, open it in a new tab
                if (source.webSiteUrl) {
                    window.open(source.webSiteUrl, '_blank');
                } else {
                    setIsLoading(true);
                    const doc = await client.downloadDocument(conversationId, source.url!);
                    const blob = new Blob([doc.data], { type: doc.data.type });
                    const file = new File([blob], source.fileName!, { type: doc.data.type });
                    const url = URL.createObjectURL(file);
                    // currently, pageNumber field is not correctly represented on the plugin, so we are not using it
                    // window.open(source.pageNumber && source.pageNumber !== "0" ? url + "#page=" + source.pageNumber : url, '_blank');
                    window.open(url, '_blank');
                    // revoke URL after a short delay to avoid memory leaks
                    setTimeout(() => URL.revokeObjectURL(url), 5000);
                }
            } catch (e: unknown) {
                dispatchNotification('error.api.get.document', 'error');
                throw e;
            } finally {
                setIsLoading(false);
            }
        });
    };

    useImperativeHandle(
        ref,
        () => ({
            openSource,
        }),
        [source, conversationId],
    );

    // Render the source preview. It includes the source title, a button to open the source, and the source content in plain text
    return (
        <div className='sourcePreviewContainer'>
            <div className={classes.sourceContainer}>
                <Subtitle1>Paragraph content</Subtitle1>
                <Body2 className={classes.body} italic>
                    "{source.chunk}"
                </Body2>
            </div>
        </div>
    );
});

export default SourcePreview;
